/**
 * Google API audit & toggle dashboard. (June 18, 2026 admin ask.)
 *
 * Three sections:
 *   1. INVENTORY  — every Google API usage in the codebase + enable toggle
 *   2. DAILY USAGE — last 14 days, grouped by capability + module
 *   3. MONTHLY USAGE — last 6 months, same grouping
 *
 * Toggles are DB-canonical. Backend enforces ``snap_to_roads`` /
 * ``directions``; frontend consumers (geocode / places / static maps /
 * maps-js viewer & picker) should call ``track('<key>', '<module>')``
 * before invoking the Google call and skip the call when the toggle
 * is off.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Globe, ToggleLeft, ToggleRight, RefreshCw, Loader2,
  Calendar, BarChart3, ServerCog, Smartphone, Server, AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GoogleApiManager() {
  const [tab, setTab] = useState('inventory');
  return (
    <div className="space-y-4" data-testid="google-api-manager">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Globe className="text-blue-600" size={22} />
          <h1 className="text-xl font-bold text-gray-900">Google API</h1>
        </div>
        <span className="text-xs text-gray-500">
          Catalogue + toggles + daily / monthly call volume by module.
          Disabling a capability stops the call from firing the next time the relevant page loads.
        </span>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { id: 'inventory', label: 'Inventory', icon: ServerCog },
          { id: 'daily',     label: 'Daily usage', icon: Calendar },
          { id: 'monthly',   label: 'Monthly usage', icon: BarChart3 },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md font-medium ${tab === t.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid={`google-api-tab-${t.id}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'inventory' && <InventoryPanel />}
      {tab === 'daily' && <UsagePanel period="daily" />}
      {tab === 'monthly' && <UsagePanel period="monthly" />}
    </div>
  );
}


function InventoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/google-api/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setItems(data.items || []);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, nextEnabled) => {
    const item = items.find((i) => i.key === key);
    const confirmMsg = nextEnabled
      ? `Enable "${item?.label}"?\n\nThe next page that needs this capability will start calling Google again.`
      : `Disable "${item?.label}"?\n\nThe next page that uses it will skip the Google call entirely. Map / autocomplete / address features for this capability will degrade.`;
    if (!window.confirm(confirmMsg)) return;
    setSavingKey(key);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/google-api/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key, enabled: nextEnabled }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      await load();
    } catch (e) { alert(`Toggle failed: ${e.message}`); }
    finally { setSavingKey(''); }
  };

  const grouped = useMemo(() => {
    const out = {};
    items.forEach((it) => {
      const k = it.surface || 'other';
      (out[k] = out[k] || []).push(it);
    });
    return out;
  }, [items]);

  if (loading) return <div className="text-sm text-gray-500">Loading inventory…</div>;
  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>;

  return (
    <div className="space-y-5" data-testid="google-api-inventory-panel">
      {['backend', 'frontend'].map((surface) => (
        <div key={surface}>
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-600 uppercase">
            {surface === 'backend' ? <Server size={12} /> : <Smartphone size={12} />}
            {surface}
          </div>
          <div className="space-y-2">
            {(grouped[surface] || []).map((it) => (
              <div
                key={it.key}
                className={`border rounded-lg p-3 ${it.enabled ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50'}`}
                data-testid={`google-api-row-${it.key}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{it.label}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono">
                        {it.key}
                      </span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                        {it.module}
                      </span>
                      <CostTier tier={it.cost_tier} />
                      {!it.enabled && (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 flex items-center gap-1">
                          <AlertTriangle size={10} /> disabled
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">{it.service}</div>
                    <div className="text-sm text-gray-800 mt-1">{it.purpose}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      <strong>Frequency:</strong> {it.frequency}
                    </div>
                  </div>
                  <button
                    onClick={() => toggle(it.key, !it.enabled)}
                    disabled={savingKey === it.key}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold ${it.enabled ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800' : 'bg-red-100 hover:bg-red-200 text-red-800'}`}
                    data-testid={`google-api-toggle-${it.key}`}
                  >
                    {savingKey === it.key ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : it.enabled ? (
                      <ToggleRight size={14} />
                    ) : (
                      <ToggleLeft size={14} />
                    )}
                    {it.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function CostTier({ tier }) {
  if (!tier) return null;
  const map = {
    low:    { txt: 'low cost',    cls: 'bg-emerald-100 text-emerald-800' },
    medium: { txt: 'medium cost', cls: 'bg-amber-100 text-amber-900' },
    high:   { txt: 'high cost',   cls: 'bg-red-100 text-red-800' },
  };
  const m = map[tier] || map.medium;
  return <span className={`text-[11px] px-1.5 py-0.5 rounded ${m.cls}`}>{m.txt}</span>;
}


function UsagePanel({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [windowSize, setWindowSize] = useState(period === 'daily' ? 14 : 6);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ period });
      if (period === 'daily') qs.set('days', String(windowSize));
      else qs.set('months', String(windowSize));
      const r = await fetch(`${API_URL}/api/admin/google-api/usage?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setData(data);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [period, windowSize]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-gray-500">Loading usage…</div>;
  if (error) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>;

  return (
    <div className="space-y-3" data-testid={`google-api-usage-${period}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-600">
          Window: <strong>{data?.window_start}</strong> → today · grand total{' '}
          <strong>{(data?.grand_total ?? 0).toLocaleString()}</strong> calls
        </span>
        <div className="flex-1" />
        <label className="text-xs text-gray-600 flex items-center gap-1">
          {period === 'daily' ? 'Days' : 'Months'}:
          <select
            value={windowSize}
            onChange={(e) => setWindowSize(parseInt(e.target.value, 10))}
            className="text-xs border border-gray-200 rounded px-1.5 py-0.5"
            data-testid={`usage-${period}-window`}
          >
            {(period === 'daily' ? [7, 14, 30, 60, 90] : [3, 6, 12, 24]).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button
          onClick={load}
          className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1"
          data-testid={`usage-${period}-refresh`}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Totals by key */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs text-gray-600">
              <th className="px-3 py-1.5">Capability</th>
              <th className="px-3 py-1.5 text-right">Calls</th>
              <th className="px-3 py-1.5 text-right">OK</th>
              <th className="px-3 py-1.5 text-right">Failed</th>
            </tr>
          </thead>
          <tbody>
            {(data?.totals_by_key || []).map((t) => (
              <tr key={t.key} className="border-t border-gray-100" data-testid={`usage-${period}-total-${t.key}`}>
                <td className="px-3 py-1.5 font-mono text-xs">{t.key}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{t.calls.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right text-emerald-700">{t.ok.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right text-red-700">{t.fail.toLocaleString()}</td>
              </tr>
            ))}
            {(data?.totals_by_key || []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-3 text-xs text-gray-500 italic text-center">No calls in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Per-period breakdown */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 max-h-[420px] overflow-y-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr className="text-left text-xs text-gray-600">
              <th className="px-3 py-1.5">{period === 'daily' ? 'Date' : 'Month'}</th>
              <th className="px-3 py-1.5">Capability</th>
              <th className="px-3 py-1.5">Module</th>
              <th className="px-3 py-1.5 text-right">Calls</th>
              <th className="px-3 py-1.5 text-right">OK</th>
              <th className="px-3 py-1.5 text-right">Failed</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-3 py-1.5 font-mono text-xs">{r.period}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.key}</td>
                <td className="px-3 py-1.5 text-xs">{r.module}</td>
                <td className="px-3 py-1.5 text-right">{r.calls.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right text-emerald-700">{r.ok.toLocaleString()}</td>
                <td className="px-3 py-1.5 text-right text-red-700">{r.fail.toLocaleString()}</td>
              </tr>
            ))}
            {(data?.rows || []).length === 0 && (
              <tr><td colSpan={6} className="px-3 py-3 text-xs text-gray-500 italic text-center">No calls in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
